'use client';

import Box from '@enlighten/design-system/dist/Box';
import Divider from '@enlighten/design-system/dist/Divider';
import Stack from '@enlighten/design-system/dist/Stack';
import Typography from '@enlighten/design-system/dist/Typography';
import Image, { StaticImageData } from 'next/image';
import React from 'react';

import ImgPpa from '@/assets/images/pages/trading/section01-ppa.png';
import ImgPpaMobile from '@/assets/images/pages/trading/section01-ppa-m.png';
import ImgRec from '@/assets/images/pages/trading/section01-rec.png';
import ImgRecMobile from '@/assets/images/pages/trading/section01-rec-m.png';

type Tab = {
	id: 'ppa' | 'rec';
	label: string;
	description: string;
	image: StaticImageData;
	imageMobile: StaticImageData;
	imageAlt: string;
	mobileContent: { label: string; color: string; bold?: boolean }[];
};

const TABS: Tab[] = [
	{
		id: 'ppa',
		label: '직접 PPA',
		description: `직접 PPA는 장기간 재생에너지 전력을 확보할 수 있는 방법으로서,\n재생에너지 조달의 핵심 수단으로 자리잡고 있습니다.`,
		image: ImgPpa,
		imageMobile: ImgPpaMobile,
		imageAlt: '엔라이튼을 중심으로 발전사업자, 수요기업, 한국전력, 전력거래소(KPX), 한국에너지공단 간의 전력 거래 및 대금 흐름을 나타낸 구조도',
		mobileContent: [
			{ label: 'a. 대금', color: '#E52F2F', bold: true },
			{ label: 'b. 전력공급', color: '#6400FF', bold: true },
			{ label: 'c. REC 비용 지급', color: '#6400FF', bold: true },
			{ label: 'd. 초과 발전량', color: '#6400FF', bold: true },
			{ label: 'e. 계약정보제공', color: '#535659' },
			{ label: 'f. 재생에너지 사용 확인서 발급', color: '#08B468', bold: true },
			{ label: 'g. 망 이용요금', color: '#E52F2F', bold: true },
			{ label: 'h. 물리적 전기공급', color: '#535659' },
			{ label: 'i. 물리적 전기공급 : 부족전력 보완', color: '#535659' },
		],
	},
	{
		id: 'rec',
		label: 'REC',
		description: `재생에너지 공급인증서 (REC)는 가장 빠르게 재생에너지를 조달하는 방안으로,\n엔라이튼은 기업이 원하는 물량을 효율적으로 매칭합니다. `,
		image: ImgRec,
		imageMobile: ImgRecMobile,
		imageAlt: '엔라이튼을 중심으로 발전사업자, 수요기업, 한국에너지공단 간의 REC 조달·이관 및 거래대금 흐름을 나타낸 구조도',
		mobileContent: [
			{ label: 'a. REC 거래 수수료', color: '#E52F2F', bold: true },
			{ label: 'b. REC 이관', color: '#6400FF', bold: true },
			{ label: 'c. REC 조달', color: '#6400FF', bold: true },
			{ label: 'd. 재생에너지 사용 확인서 발급', color: '#08B468', bold: true },
			{ label: 'e. 매수/매도 계약 주문서 작성', color: '#535659' },
		],
	},
];

const Section1Tabs = () => {
	const [activeTab, setActiveTab] = React.useState(TABS[0]);

	const handleTabClick = (tab: Tab) => {
		setActiveTab(tab);
	};

	return (
		<>
			<Box
				maxWidth="450px"
				width="100%"
				height={{ xs: 'auto', md: '100px' }}
				display="flex"
				alignItems="center"
				padding={{ xs: 0, md: '12px 20px' }}
				borderRadius="100px"
				sx={{
					boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.05)',
				}}
			>
				{TABS.map((tab) => {
					const isActive = activeTab.id === tab.id;

					return (
						<Box
							key={tab.id}
							display="flex"
							alignItems="center"
							justifyContent="center"
							height={{ xs: '64px', md: '76px' }}
							flex="1"
							bgcolor={isActive ? '#6400FF' : 'transparent'}
							borderRadius="100px"
							sx={{
								cursor: 'pointer',
							}}
							onClick={() => handleTabClick(tab)}
						>
							<Typography fontWeight="bold" fontSize={{ xs: '16px', md: '20px' }} color={isActive ? '#ffffff' : '#73767A'}>
								{tab.label}
							</Typography>
						</Box>
					);
				})}
			</Box>

			<Stack
				alignItems="center"
				width="100%"
				maxWidth="1392px"
				marginX="auto"
				padding={{ xs: '32px 24px 40px', md: '56px 56px 98px' }}
				border={{ xs: '1px solid #6400FF', md: '2px solid #6400FF' }}
				borderRadius="40px"
				sx={{
					boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.05)',
				}}
			>
				<Stack gap={{ xs: '8px', md: '12px' }} textAlign="center">
					<Typography fontSize={{ xs: '24px', md: '30px' }} fontWeight="bold">
						{activeTab.label}
					</Typography>
					<Typography fontSize={{ xs: '14px', md: '16px' }} color="#73767A" lineHeight={{ xs: '22px', md: '26px' }}>
						{activeTab.description}
					</Typography>
				</Stack>
				<Divider flexItem sx={{ margin: { xs: '24px 0', md: '40px 0 52px' } }} />
				<Box
					sx={{
						'& img': {
							width: '100%',
							height: 'auto',
						},
					}}
				>
					<Box component={Image} src={activeTab.image} alt={activeTab.imageAlt} width={0} height={0} display={{ xs: 'none', md: 'block' }} />
					<Box component={Image} src={activeTab.imageMobile} alt={activeTab.imageAlt} width={0} height={0} display={{ xs: 'block', md: 'none' }} />
				</Box>
				<Stack component="ul" display={{ xs: 'flex', md: 'none' }} gap="12px" width="100%" padding={0} margin={0} mt="30px" sx={{ listStyle: 'none' }}>
					{activeTab.mobileContent.map((item, index) => (
						<Typography key={index} component="li" fontSize="14px" fontWeight={item.bold ? 'bold' : 'normal'} color={item.color}>
							{item.label}
						</Typography>
					))}
				</Stack>
			</Stack>
		</>
	);
};

export default Section1Tabs;
